import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DuplicateGroup,
  errorMessage,
  fetchTopicDedup,
  fetchTopicForecast,
  fetchTopicThemes,
  ForecastPoint,
} from './api';
import { trendSourceLabel } from './trendSources';

const numFmt = new Intl.NumberFormat('it-IT');
const compactDateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });

function confidenceLabel(value: number): string {
  if (value < 0.3) return 'Bassa';
  if (value < 0.65) return 'Media';
  return 'Alta';
}

function Sparkline({ series }: { series: ForecastPoint[] }) {
  const width = 240;
  const height = 58;
  const pad = 4;
  const max = Math.max(1, ...series.map((point) => point.count));
  const points = series.map((point, index) => {
    const x = pad + index / Math.max(1, series.length - 1) * (width - pad * 2);
    const y = height - pad - point.count / max * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return (
    <svg className="forecast-sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Andamento giornaliero osservato">
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} />
      {points && <polyline points={points} />}
    </svg>
  );
}

function SourceBadges({ sources }: { sources: string[] }) {
  return <span className="signal-sources">{sources.map((source) => <span key={source}>{trendSourceLabel(source)}</span>)}</span>;
}

function SampleLinks({ urls }: { urls: string[] }) {
  if (urls.length === 0) return null;
  return <span className="signal-links">{urls.slice(0, 4).map((url, index) => <a key={url} href={url} target="_blank" rel="noopener noreferrer" aria-label={`Apri esempio ${index + 1}`}>{index + 1}</a>)}</span>;
}

function CrossSourceStory({ story }: { story: DuplicateGroup }) {
  return (
    <article className="cross-source-row">
      <div><strong>{story.label}</strong><small>{story.size} occorrenze su {story.sourceCount} fonti</small></div>
      <div><SourceBadges sources={story.sources} /><SampleLinks urls={story.sampleUrls} /></div>
    </article>
  );
}

export default function TopicIntelligence({ topicId }: { topicId: string }) {
  const queryClient = useQueryClient();
  const [started, setStarted] = useState(() => Boolean(
    queryClient.getQueryData(['topic-forecast', topicId])
    || queryClient.getQueryData(['topic-themes', topicId])
    || queryClient.getQueryData(['topic-dedup', topicId]),
  ));
  const themes = useQuery({
    queryKey: ['topic-themes', topicId],
    queryFn: () => fetchTopicThemes(topicId),
    enabled: started,
    staleTime: 10 * 60_000,
  });
  const dedup = useQuery({
    queryKey: ['topic-dedup', topicId],
    queryFn: () => fetchTopicDedup(topicId),
    enabled: started,
    staleTime: 10 * 60_000,
  });
  const forecast = useQuery({
    queryKey: ['topic-forecast', topicId],
    queryFn: () => fetchTopicForecast(topicId),
    enabled: started,
    staleTime: 10 * 60_000,
  });

  const isPending = started && (themes.isPending || dedup.isPending || forecast.isPending);
  const hasError = themes.isError || dedup.isError || forecast.isError;
  const retry = () => {
    if (themes.isError) themes.refetch();
    if (dedup.isError) dedup.refetch();
    if (forecast.isError) forecast.refetch();
  };

  if (!started) {
    return (
      <section className="topic-intelligence-intro">
        <div><p className="eyebrow">Approfondimento</p><h4>Leggi cosa c’è dietro al volume</h4><p>Raggruppa i sotto-temi, elimina le repliche e calcola una direzione stimata. L’AI viene usata solo per creare gli embedding mancanti.</p></div>
        <button type="button" className="btn-secondary" onClick={() => setStarted(true)}>Analizza segnali</button>
      </section>
    );
  }

  return (
    <section className="topic-intelligence" aria-labelledby={`topic-intelligence-${topicId}`}>
      <div className="topic-intelligence-head"><div><p className="eyebrow">Approfondimento</p><h4 id={`topic-intelligence-${topicId}`}>Intelligence del topic</h4></div>{!isPending && <button type="button" className="link-btn" onClick={() => { themes.refetch(); dedup.refetch(); forecast.refetch(); }}>Aggiorna analisi</button>}</div>

      {isPending && <div className="intelligence-loading skeleton" role="status"><span className="sr-only">Analisi avanzata del topic</span><div className="sk-line sk-w40" /><div className="sk-line" /><div className="sk-line sk-w60" /></div>}
      {hasError && <div className="intelligence-error"><p className="error">Una parte dell’analisi non è disponibile. {errorMessage(themes.error ?? dedup.error ?? forecast.error)}</p><button type="button" className="link-btn" onClick={retry}>Riprova dati mancanti</button></div>}

      {forecast.isSuccess && (
        <section className="forecast-block" aria-labelledby={`forecast-${topicId}`}>
          <div className="signal-section-head"><div><h5 id={`forecast-${topicId}`}>Direzione prossimi 7 giorni</h5><p>Modello statistico sui {forecast.data.observedDays} giorni osservati</p></div><span className="evidence-badge estimated">Stima</span></div>
          <div className="forecast-layout">
            <Sparkline series={forecast.data.series} />
            <dl className="forecast-facts">
              <div><dt>Direzione</dt><dd>{forecast.data.totalItems < 8 ? 'Dati ancora limitati' : forecast.data.direction}</dd></div>
              <div><dt>Volume atteso</dt><dd>{numFmt.format(forecast.data.projectedNext7d)} <small>contenuti</small></dd></div>
              <div><dt>Affidabilità</dt><dd>{confidenceLabel(forecast.data.confidence)} <small>{Math.round(forecast.data.confidence * 100)}%</small></dd></div>
            </dl>
          </div>
          {(forecast.data.confidence < 0.3 || forecast.data.totalItems < 8) && <p className="forecast-caution">I dati disponibili non bastano ancora per una proiezione affidabile. Usa il valore come indicazione preliminare.</p>}
        </section>
      )}

      {themes.isSuccess && (
        <section className="signal-section" aria-labelledby={`themes-${topicId}`}>
          <div className="signal-section-head"><div><h5 id={`themes-${topicId}`}>Sotto-temi emergenti</h5><p>{numFmt.format(themes.data.itemsAnalyzed)} contenuti analizzati</p></div><span>{themes.data.themeCount} temi</span></div>
          {themes.data.themeCount === 0 ? <p className="muted">Ancora pochi contenuti per estrarre temi coerenti.</p> : <div className="theme-list">{themes.data.themes.slice(0, 8).map((theme) => <article className="theme-row" key={theme.id}><span className="theme-rank">{theme.id}</span><div><strong>{theme.label}</strong><p>{numFmt.format(theme.size)} contenuti · {numFmt.format(theme.last7d)} negli ultimi 7 giorni{theme.latestAt ? ` · ultimo ${compactDateFmt.format(new Date(theme.latestAt))}` : ''}</p><div><SourceBadges sources={theme.sources} /><SampleLinks urls={theme.sampleUrls} /></div></div></article>)}</div>}
          {themes.data.embeddedNow > 0 && <p className="embedding-note">{numFmt.format(themes.data.embeddedNow)} contenuti indicizzati semanticamente in questa analisi.</p>}
        </section>
      )}

      {dedup.isSuccess && (
        <section className="signal-section" aria-labelledby={`dedup-${topicId}`}>
          <div className="signal-section-head"><div><h5 id={`dedup-${topicId}`}>Storie uniche e rimbalzi</h5><p>Il volume viene corretto per evitare che le repliche sembrino segnali nuovi</p></div><span className="evidence-badge detected">Calcolato</span></div>
          <dl className="dedup-summary"><div><dt>Storie uniche</dt><dd>{numFmt.format(dedup.data.uniqueCount)} <small>su {numFmt.format(dedup.data.rawCount)}</small></dd></div><div><dt>Repliche</dt><dd>{numFmt.format(dedup.data.duplicateItems)}</dd></div><div><dt>Cross-fonte</dt><dd>{numFmt.format(dedup.data.crossSourceStories.length)}</dd></div></dl>
          {dedup.data.crossSourceStories.length > 0 ? <><h6>Rimbalza su più fonti</h6><div className="cross-source-list">{dedup.data.crossSourceStories.slice(0, 6).map((story, index) => <CrossSourceStory key={`${story.label}-${index}`} story={story} />)}</div></> : <p className="muted">Nessuna storia è ancora presente su più fonti.</p>}
        </section>
      )}
    </section>
  );
}
